import { getHeaders, getUserDetails } from 'auth/auth';
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../baseQuery';

export const documentAPI = createApi({
  reducerPath: 'Document',
  tagTypes: ['updateList'],
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    // Legacy aggregate endpoint used for B2B flows
    getDocumentList: builder.query({
      query: ({ applicationId, borrowerId } = {}) => {
        const qs = borrowerId ? `?borrower_id=${borrowerId}` : '';
        return {
          url: `/b2b/documents/aggregate/loan/${applicationId}${qs}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      providesTags: ['updateList'],
    }),
    // B2C v2 endpoint using aggregate-style grouped payload under `documents`
    getB2cDocumentList: builder.query({
      query: ({ applicationId, borrowerId } = {}) => {
        const params = {};
        if (applicationId) params.applicationId = applicationId;
        if (borrowerId) params.borrowerId = borrowerId;
        return {
          url: `/b2c/loan/documents`,
          method: 'GET',
          params,
          headers: getHeaders(),
        };
      },
      transformResponse: (response) => ({
        documents: response?.documents ?? [],
        total: Array.isArray(response?.documents)
          ? response.documents.length
          : 0,
        skip: 0,
        limit: Array.isArray(response?.documents)
          ? response.documents.length
          : 0,
      }),
      providesTags: ['updateList'],
      keepUnusedDataFor: 300, // 5 minutes cache persistence
    }),
    getUserProperty: builder.query({
      query: () => {
        return {
          url: `/b2b/documents/user_info`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),
    deleteDocument: builder.mutation({
      query: ({ documentId }) => {
        const headers = getHeaders();
        delete headers.accept;
        headers.Accept = 'application/json';
        return {
          url: `/b2b/documents/${documentId}`,
          method: 'DELETE',
          headers,
        };
      },
      invalidatesTags: ['updateList'],
    }),
    deleteB2cDocument: builder.mutation({
      query: ({ documentId }) => {
        const headers = getHeaders();
        delete headers.accept;
        headers.Accept = 'application/json';
        return {
          url: `/b2c/loan/documents/${documentId}`,
          method: 'DELETE',
          headers,
        };
      },
      invalidatesTags: ['updateList'],
    }),
    getDocumentById: builder.query({
      query: ({ documentId }) => ({
        url: `/b2b/documents/get`,
        method: 'GET',
        params: { document_id: documentId },
        headers: getHeaders(),
      }),
    }),
    getB2cDocumentById: builder.query({
      query: ({ documentId }) => ({
        url: `/b2c/documents/get`,
        method: 'GET',
        params: { document_id: documentId },
        headers: getHeaders(),
      }),
    }),
    uploadPredefinedDocs: builder.mutation({
      query: ({ data, additionalParams }) => {
        const formData = new FormData();
        formData.append('file', data);
        Object.keys(additionalParams || {}).forEach((key) => {
          if (
            additionalParams[key] !== undefined &&
            additionalParams[key] !== null &&
            additionalParams[key] !== ''
          ) {
            formData.append(key, additionalParams[key]);
          }
        });
        const headers = getHeaders();
        delete headers['Content-Type'];
        delete headers['content-type'];
        return {
          url: `/b2b/documents/upload`,
          method: 'POST',
          headers,
          body: formData,
        };
      },
      invalidatesTags: ['updateList'],
    }),
    uploadB2cDocument: builder.mutation({
      query: ({ data, additionalParams }) => {
        const formData = new FormData();
        formData.append('file', data);
        const {
          document_category_id,
          document_type_id,
          documentTypeCode,
          documentTitle,
          title,
          applicationId,
          borrowerId,
          tags,
          ...remainingParams
        } = additionalParams || {};

        const appendedKeys = new Set();
        const appendIfDefined = (key, value) => {
          if (value !== undefined && value !== null && value !== '') {
            formData.append(key, value);
            appendedKeys.add(key);
          }
        };

        appendIfDefined('document_category_id', document_category_id);
        appendIfDefined('document_type_id', document_type_id);
        appendIfDefined('document_type_code', documentTypeCode);
        appendIfDefined('title', documentTitle || title);
        appendIfDefined('applicationId', applicationId);
        appendIfDefined('borrowerId', borrowerId);

        if (Array.isArray(tags) && tags.length > 0) {
          appendIfDefined('tags', tags.join(','));
        }

        Object.entries(remainingParams).forEach(([key, value]) => {
          if (value !== undefined && value !== null && !appendedKeys.has(key)) {
            formData.append(key, value);
          }
        });

        const headers = getHeaders();
        delete headers['Content-Type'];
        delete headers['content-type'];
        return {
          url: `/b2c/loan/documents/upload`,
          method: 'POST',
          headers,
          body: formData,
        };
      },
      invalidatesTags: ['updateList'],
    }),
    updatePredefinedDocs: builder.mutation({
      query: ({ data, additionalParams, documentId }) => {
        const formData = new FormData();
        formData.append('file', data);
        Object.keys(additionalParams || {}).forEach((key) => {
          if (
            additionalParams[key] !== undefined &&
            additionalParams[key] !== null &&
            additionalParams[key] !== ''
          ) {
            formData.append(key, additionalParams[key]);
          }
        });
        return {
          url: `/b2b/documents/update_document_file/${documentId}`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['updateList'],
    }),
    readFile: builder.mutation({
      query: ({ document_id }) => ({
        url: `/b2b/documents/download`,
        method: 'GET',
        params: { document_id },
        headers: getHeaders(),
        responseHandler: (response) => response.blob(),
      }),
    }),
  }),
});

// api for view document is present in hooks > pdfViewer.js

export const {
  useGetDocumentListQuery,
  useGetB2cDocumentListQuery,
  useGetDocumentByIdQuery,
  useGetB2cDocumentByIdQuery,
  useLazyGetDocumentByIdQuery,
  useLazyGetB2cDocumentByIdQuery,
  useUploadPredefinedDocsMutation,
  useUploadB2cDocumentMutation,
  useUpdatePredefinedDocsMutation,
  useGetUserPropertyQuery,
  useReadFileMutation,
  useDeleteDocumentMutation,
  useDeleteB2cDocumentMutation,
} = documentAPI;
